import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { motion } from "framer-motion";
import { TextField, MenuItem, InputAdornment, Box } from "@mui/material";
import { 
  MdBusiness, MdEmail, MdLock, MdCategory, MdPerson, 
  MdPhone, MdLocationOn, MdDescription, MdTitle, MdCalendarToday,
  MdAttachMoney, MdCode, MdLink, MdNotes
} from "react-icons/md";

const iconMap = {
  name: <MdPerson />,
  business_name: <MdBusiness />,
  owner_name: <MdPerson />,
  email: <MdEmail />,
  password: <MdLock />,
  confirm_password: <MdLock />,
  business_type: <MdCategory />,
  contact: <MdPhone />,
  phone: <MdPhone />,
  address: <MdLocationOn />,
  description: <MdDescription />,
  title: <MdTitle />,
  date: <MdCalendarToday />,
  price: <MdAttachMoney />,
  amount: <MdAttachMoney />,
  code: <MdCode />,
  url: <MdLink />,
  notes: <MdNotes />,
};

const ModernFormField = ({ field, form, comp, index }) => {
  const getIcon = () => {
    return iconMap[comp.name] || iconMap[comp.type] || <MdCategory />;
  };

  if (comp.type === 'select') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: 0.3 }}
        className="w-full"
      >
        <TextField
          {...field}
          select
          fullWidth
          label={comp.label}
          variant="outlined"
          error={form.touched[comp.name] && !!form.errors[comp.name]}
          helperText={form.touched[comp.name] && form.errors[comp.name]}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.15)',
              },
              '&.Mui-focused': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.25)',
              },
            },
            '& .MuiInputLabel-root': {
              color: '#666',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#667eea',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <span className="text-[#667eea] text-xl">{getIcon()}</span>
              </InputAdornment>
            ),
          }}
        >
          {comp.options?.map((option) => (
            <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.3 }}
      className="w-full"
    >
      <TextField
        {...field}
        type={comp.type || 'text'}
        fullWidth
        label={comp.label}
        placeholder={comp.placeholder}
        variant="outlined"
        error={form.touched[comp.name] && !!form.errors[comp.name]}
        helperText={form.touched[comp.name] && form.errors[comp.name]}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.15)',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.25)',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#666',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#667eea',
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <span className="text-[#667eea] text-xl">{getIcon()}</span>
            </InputAdornment>
          ),
        }}
      />
    </motion.div>
  );
};

const FormikForm = ({initialValues,onSubmit,fields,extra_element,action,validationSchema}) => {
  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ touched, errors, isSubmitting, values, isValid, setFieldValue, setFieldTouched }) => {
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full"
          >
            <Form className="flex flex-col gap-6 w-full">
              {fields.map((e, i) => (
                <Field key={i} name={e.name}>
                  {({ field, form }) => (
                    <ModernFormField field={field} form={form} comp={e} index={i} />
                  )}
                </Field>
              ))}
              {extra_element}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: fields.length * 0.1 }}
                className="w-full mt-4"
              >
                {action(isSubmitting)}
              </motion.div>
            </Form>
          </motion.div>
        );
      }}
    </Formik>
  );
};

export default FormikForm;